import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

import { calculateAgeAndDOB } from "react-lib/apps/HISV3/common/CommonInterface";

import { stripHtml } from "../patient/resultModelV4";

export type RegistrationMenuKey = "pos" | "info" | "encounter" | "history";

/** แถวของ PatientList.list (SetPatientSequence.patientList) */
export type PatientListItem = {
  id: number;
  address?: string;
  birthdate?: string;
  citizen_passport?: string;
  full_name?: string;
  hn?: string;
  mother?: string;
  old_name?: string;
  phone_num?: string;
  present_mobile_no?: string;
  /** VERIFIED / NOT_VERIFIED (ยืนยันตัวตนผ่านแอป) */
  profile_status?: string;
  tel_num?: string;
};

/** แถวเปิดประวัติหมู่: จากไฟล์ xlsx หรือผลของ register_emergency */
export type GroupRow = {
  no: number;
  first_name_th: string;
  gender: string;
  hn: string;
  last_name_th: string;
  pre_name: string;
};

/** SetPatientSequence.patient (PatientDetailView) */
export type PatientDetail = Partial<PatientSerializer> & { phone_num?: string };

export type PatientSummary = {
  id?: number;
  hn: string;
  image: string;
  name: string;
  rows: { key: string; value: string }[];
};

/** แถวของ appointmentEncounterList (CreateUpdateEncounter) */
export type AppointmentItem = Omit<Partial<PatientAppointmentSerializer>, "appointment_type"> & {
  appointment_type?: number | string;
  children?: { summary_detail?: string }[];
  detail?: string;
  doctor?: number | null;
  doctor_name?: string;
  order_dict?: Record<string, string>;
};

export type AppointmentRow = {
  id: number;
  date: string;
  detail: string;
  division: string;
  doctor: string;
  orders: string;
  time: string;
};

export type EncounterItem = Partial<EncounterSerializer> & { id: number };

export const REGISTRATION_MENU: {
  key: RegistrationMenuKey;
  title: string;
  subtitle: string;
  icon: string;
}[] = [
  {
    key: "pos",
    title: "เลือกซื้อสินค้าและบริการ",
    subtitle: "POS · อาหารเสริม · แพ็กเกจ",
    icon: "🛒",
  },
  { key: "info", title: "ข้อมูลผู้รับบริการ", subtitle: "ข้อมูลบัตร · ที่อยู่", icon: "👤" },
  { key: "history", title: "ดูประวัติการรักษา", subtitle: "Patient History Viewer", icon: "📋" },
  { key: "encounter", title: "เปิด Encounter", subtitle: "เลือกนัด / Walk-in", icon: "🩺" },
];

/** เมนู POS (MENU_KEYS.PRODUCT_SERVICE ใน CardMeBillingMenuContent) */
export const POS_MENU = "เลือกสินค้าและบริการ";

/** แถวจากไฟล์ xlsx (หัวคอลัมน์ คำนำหน้า / ชื่อ / นามสกุล / เพศ) */
export const formatGroupRowsFromSheet = (rows: Record<string, unknown>[]): GroupRow[] =>
  rows.map((row, index) => ({
    ...row,
    no: index + 1,
    first_name_th: String(row["ชื่อ"] ?? ""),
    gender: String(row["เพศ"] ?? ""),
    hn: "",
    last_name_th: String(row["นามสกุล"] ?? ""),
    pre_name: String(row["คำนำหน้า"] ?? ""),
  }));

/** ผลจาก register_emergency (patientEmergencyList) มี hn ของแต่ละคนแล้ว */
export const formatGroupRows = (list: Partial<GroupRow>[]): GroupRow[] =>
  list.map((item, index) => ({
    no: index + 1,
    first_name_th: item.first_name_th ?? "",
    gender: item.gender ?? "",
    hn: item.hn ?? "",
    last_name_th: item.last_name_th ?? "",
    pre_name: item.pre_name ?? "",
  }));

export const getPatientSummary = (patient: PatientDetail): PatientSummary => {
  const present = patient.present_address;
  const age = calculateAgeAndDOB(patient.full_age).fullage_th;

  return {
    id: patient.id,
    hn: patient.hn ?? "",
    image: patient.profile_image?.image ?? "",
    name: patient.full_name ?? "",
    rows: [
      { key: "เพศ", value: patient.gender_name || "-" },
      { key: "อายุ", value: age || "-" },
      { key: "วันเกิด", value: patient.birthdate || "-" },
      { key: "เบอร์โทร", value: present?.tel_mobile || patient.phone_num || "-" },
      { key: "E-mail", value: present?.email || patient.email || "-" },
      { key: "Citizen ID", value: patient.citizen_passport || patient.citizen_no || "-" },
    ],
  };
};

/** นัดที่เปิด Encounter ได้: มีวัน เวลา แพทย์ หรือเป็นนัดแพ็กเกจ / กายภาพ */
export const formatAppointmentRows = (list: AppointmentItem[]): AppointmentRow[] =>
  list
    .filter((item) => {
      const hasDetails = !!(item.date && item.start_time && item.doctor);
      const isPackage = Number(item.appointment_type) === 3;
      const isRehab = Object.values(item.order_dict ?? {}).includes("rehabilitationorder");

      return !!item.id && (hasDetails || isPackage || isRehab);
    })
    .map((item) => ({
      id: item.id as number,
      date: item.date ?? "",
      detail: item.is_telemed ? "นัดปรึกษาแพทย์ออนไลน์" : (item.detail ?? ""),
      division: item.division_name ?? "",
      doctor: item.doctor_name ?? "",
      orders: (item.children ?? [])
        .map((child) => stripHtml(child.summary_detail))
        .filter(Boolean)
        .join(" · "),
      time: item.start_time ?? "",
    }));
